import { useCallback, useInsertionEffect, useRef } from "react";

/**
 * A handler whose identity never changes while it always sees the latest
 * render's values.
 *
 * ChatView re-renders on every provider delta, so any handler it declares gets
 * a new identity at stream frequency. Handlers passed down to a memoed child —
 * the composer above all — must not carry that churn into the child's memo
 * boundary, and their identity is meaningless to the child anyway.
 *
 * Only for handlers: the returned function must not be called during render,
 * because the latest one is installed after the render that declared it
 * commits.
 */
export function useStableCallback<A extends (...args: never[]) => unknown>(callback: A): A {
  const latest = useRef(callback);
  useInsertionEffect(() => {
    latest.current = callback;
  });
  return useCallback(((...args) => latest.current(...args)) as A, []);
}
